
import React, { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
}

interface ParticleAnimationProps {
  isActive: boolean;
  score: number;
}

export const ParticleAnimation: React.FC<ParticleAnimationProps> = ({ isActive, score }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    if (!isActive || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const updateCanvasSize = () => {
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = canvas.offsetHeight * 2;
      ctx.scale(2, 2);
    };

    updateCanvasSize();

    const createParticle = (): Particle => {
      const angle = Math.random() * Math.PI * 2;
      const radius = 120 + Math.random() * 20;
      const centerX = canvas.offsetWidth / 2;
      const centerY = canvas.offsetHeight / 2;
      
      return {
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
        vx: (Math.random() - 0.5) * 1,
        vy: (Math.random() - 0.5) * 1,
        life: 0,
        maxLife: 80 + Math.random() * 40,
        size: 1.5 + Math.random() * 2
      };
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      // Reduce particle count and frequency
      const particleCount = Math.floor(score / 30);
      if (particlesRef.current.length < particleCount && Math.random() < 0.15) {
        particlesRef.current.push(createParticle());
      }

      // Update and draw particles with softer colors
      particlesRef.current = particlesRef.current.filter(particle => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.life++;

        const alpha = Math.max(0, 0.6 - (particle.life / particle.maxLife));
        // Softer, more natural colors
        const hue = score > 80 ? 140 : score > 60 ? 45 : 25;
        const saturation = 45; // Reduced saturation
        const lightness = 65; // More natural lightness
        
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        return particle.life < particle.maxLife;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, score]);

  if (!isActive) return null;

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ width: '100%', height: '100%' }}
    />
  );
};
